import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from './layout/Layout';

interface User {
  id: number;
  name: string;
  email: string;
  account_number: string;
  profile_picture: string | null;
}

interface Location {
  latitude: number | string;
  longitude: number | string;
  location_name: string;
  formatted: string;
}

interface LiveUser {
  id: number;
  user: User;
  location: Location | null;
  description: string;
  hosting_preference: string;
  expires_at: string;
  created_at: string;
  is_active: boolean;
  status: string;
}

interface Stats {
  active_live_users: number;
  expired_sessions: number;
  can_host_count: number;
  avg_duration_hours: string;
}

const LiveUsers: React.FC = () => {
  const [liveUsers, setLiveUsers] = useState<LiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    hostingPreference: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch data functions
  const fetchLiveUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post('/admin/live-users', {
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status,
        hostingPreference: filters.hostingPreference,
        dateFrom: filters.dateFrom || null,
        dateTo: filters.dateTo || null,
        search: filters.search
      });

      if (response.data.success) {
        setLiveUsers(response.data.data.live_users);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total,
          totalPages: response.data.data.pagination.totalPages
        }));
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Error fetching live users:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/live-users/stats');
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchLiveUsers();
    fetchStats();
  }, [fetchLiveUsers]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchLiveUsers();
      fetchStats();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchLiveUsers]);

  // Handlers
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchLiveUsers();
  };

  const resetFilters = () => {
    setFilters({
      status: 'all',
      hostingPreference: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const openGoogleMaps = (lat: number | string, lng: number | string) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  const getTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <Layout>
      <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Live Users</h4>
                <p className="text-muted mb-0">Monitor users currently seeking meetups</p>
              </div>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <a href="/dashboard" style={{ color: '#006175' }}>Nyvro</a>
                  </li>
                  <li className="breadcrumb-item active">Live Users</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row mb-4">
          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Active Live Users</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#28a745' }}>
                      {stats?.active_live_users || 0}
                    </h3>
                    <small className="text-success">
                      <i className="bx bx-radio-circle-marked me-1"></i>
                      Currently active
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#28a74520' }}>
                      <i className="bx bx-broadcast font-size-24" style={{ color: '#28a745' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Expired Sessions</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#6c757d' }}>
                      {stats?.expired_sessions || 0}
                    </h3>
                    <small className="text-muted">
                      <i className="bx bx-time me-1"></i>
                      Past sessions
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#6c757d20' }}>
                      <i className="bx bx-history font-size-24" style={{ color: '#6c757d' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Can Host</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#17a2b8' }}>
                      {stats?.can_host_count || 0}
                    </h3>
                    <small className="text-info">
                      <i className="bx bx-home me-1"></i>
                      Active hosts
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#17a2b820' }}>
                      <i className="bx bx-home-heart font-size-24" style={{ color: '#17a2b8' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6 mb-3">
            <div className="card stats-card h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-muted mb-1 fw-medium">Avg Duration</p>
                    <h3 className="mb-0 fw-bold" style={{ color: '#ffc107' }}>
                      {stats?.avg_duration_hours || '0'}h
                    </h3>
                    <small className="text-warning">
                      <i className="bx bx-timer me-1"></i>
                      Average session
                    </small>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="avatar-lg rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: '#ffc10720' }}>
                      <i className="bx bx-stopwatch font-size-24" style={{ color: '#ffc107' }}></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Auto-refresh Toggle & Last Updated */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="form-check form-switch">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="autoRefresh"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="autoRefresh">
                  Auto-refresh (30s)
                </label>
              </div>
              <small className="text-muted">
                <i className="bx bx-time-five me-1"></i>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </small>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="mb-3">
                  <i className="bx bx-filter me-2"></i>
                  Filters
                </h5>

                <div className="row g-3">
                  <div className="col-md-2">
                    <label className="form-label">Status</label>
                    <select 
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Hosting Preference</label>
                    <select 
                      className="form-select"
                      value={filters.hostingPreference}
                      onChange={(e) => handleFilterChange('hostingPreference', e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="can_host">Can Host</option>
                      <option value="cannot_host">Cannot Host</option>
                    </select>
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">From Date</label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                  </div>

                  <div className="col-md-2">
                    <label className="form-label">To Date</label>
                    <input 
                      type="date" 
                      className="form-control"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Search</label>
                    <input 
                      type="text" 
                      className="form-control"
                      placeholder="User, location..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <button className="btn btn-primary me-2" onClick={applyFilters}>
                    <i className="bx bx-filter me-1"></i>
                    Apply Filters
                  </button>
                  <button className="btn btn-secondary" onClick={resetFilters}>
                    <i className="bx bx-reset me-1"></i>
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="mb-3">
                  <i className="bx bx-list-ul me-2"></i>
                  Live Users ({pagination.total})
                </h5>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : liveUsers.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-user-x font-size-48 text-muted mb-3 d-block"></i>
                    <p className="text-muted">No live users found</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>User</th>
                            <th>Location</th>
                            <th>Description</th>
                            <th>Hosting</th>
                            <th>Status</th>
                            <th>Expires At</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {liveUsers.map(liveUser => (
                            <tr key={liveUser.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img
                                    src={liveUser.user.profile_picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(liveUser.user.name)}&background=667eea&color=fff&size=40&bold=true`}
                                    className="rounded-circle me-2"
                                    style={{ width: '40px', height: '40px', objectFit: 'cover', cursor: 'pointer' }}
                                    alt={liveUser.user.name}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(liveUser.user.name)}&background=667eea&color=fff&size=40&bold=true`;
                                    }}
                                    onClick={() => window.location.href = `/users?userId=${liveUser.user.id}`}
                                    title="View user details"
                                  />
                                  <div>
                                    <div className="fw-semibold">{liveUser.user.name}</div>
                                    <small className="text-muted">{liveUser.user.email}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {liveUser.location ? (
                                  <div className="d-flex align-items-center">
                                    <div className="me-2">
                                      <div>{liveUser.location.formatted}</div>
                                      <small className="text-muted">
                                        {parseFloat(liveUser.location.latitude.toString()).toFixed(4)}, {parseFloat(liveUser.location.longitude.toString()).toFixed(4)}
                                      </small>
                                    </div>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => openGoogleMaps(liveUser.location!.latitude, liveUser.location!.longitude)}
                                      title="View on Google Maps"
                                    >
                                      <i className="bx bx-map"></i>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-muted">No location</span>
                                )}
                              </td>
                              <td>
                                <div style={{ maxWidth: '200px' }}>
                                  <p className="mb-0 text-truncate" title={liveUser.description}>
                                    {liveUser.description}
                                  </p>
                                </div>
                              </td>
                              <td>
                                {liveUser.hosting_preference === 'can_host' ? (
                                  <span className="badge bg-success">
                                    <i className="bx bx-home me-1"></i>
                                    Can Host
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary">
                                    <i className="bx bx-x me-1"></i>
                                    Cannot Host
                                  </span>
                                )}
                              </td>
                              <td>
                                {liveUser.status === 'active' ? (
                                  <span className="badge bg-success">
                                    <i className="bx bx-radio-circle-marked me-1"></i>
                                    Active
                                  </span>
                                ) : (
                                  <span className="badge bg-danger">
                                    <i className="bx bx-x-circle me-1"></i>
                                    Expired
                                  </span>
                                )}
                              </td>
                              <td>
                                <div>
                                  <div>{new Date(liveUser.expires_at).toLocaleString()}</div>
                                  {liveUser.status === 'active' && (
                                    <small className="text-success">
                                      <i className="bx bx-timer me-1"></i>
                                      {getTimeRemaining(liveUser.expires_at)} remaining
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <small>{new Date(liveUser.created_at).toLocaleString()}</small>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="mt-3">
                        <nav>
                          <ul className="pagination justify-content-center mb-0">
                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page === 1}
                              >
                                Previous
                              </button>
                            </li>
                            {Array.from({ length: Math.min(pagination.totalPages, 10) }, (_, i) => {
                              const page = i + 1;
                              if (pagination.totalPages <= 10 || page === 1 || page === pagination.totalPages || 
                                  (page >= pagination.page - 2 && page <= pagination.page + 2)) {
                                return (
                                  <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link"
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              } else if (page === pagination.page - 3 || page === pagination.page + 3) {
                                return <li key={page} className="page-item disabled"><span className="page-link">...</span></li>;
                              }
                              return null;
                            })}
                            <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link"
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={pagination.page === pagination.totalPages}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
    </Layout>
  );
};

export default LiveUsers;
